import React, {useRef, useEffect, useCallback} from 'react';
import { useHistory } from 'react-router-dom';
import styles from './HomeParallax.module.scss';

import IntroText from "components/homePanels/c_introText/IntroText";
import MacroPage from "components/homePanels/f-i_macroPage/MacroPage";
import MicroInfluencers from "components/homePanels/j_microInfluencers/MicroInfluencers";
import Telephone from "components/homePanels/k_telephone/Telephone";
import Featured from "components/homePanels/d_featured/Featured";
import OriginalsUGC from "components/homePanels/e_contest/OriginalsUGC";

import ScrollEvent from "utils/ScrollEvent";
import CustomWraps from "components/homePanels/l_customWraps/CustomWraps";
import NavToggle from "components/icons/navToggle/NavToggle";
import NavMenu from "components/navMenu/NavMenu";

import {useDispatch, useSelector} from "react-redux";
import {defaultOrder} from "../../redux/PanelsReducer";
import {analyzeStack, buttonClick, forceRedraw} from "utils/Helpers";
import {gsap} from "gsap";
import HeaderLogo from "components/headerLogo/HeaderLogo";
import ScrollIcon from "components/icons/scrollIcon/ScrollIcon";
import parallaxStyles from "components/homeParallax/HomeParallax.module.scss";
import LandingVideo from "components/videoPlayers/landingVideo/LandingVideo";
import BackgroundMusic from "components/backgroundMusic/BackgroundMusic";

const HomeParallax = (props) => {

    let history = useHistory();
    const parallax = useRef();

    const dispatch = useDispatch();
    const panelData = useSelector(state => state.panelData);
    const accessible = useSelector(state => state.accessible);
    const animationCount = useSelector(state => state.animationCount);


    const toggle = useRef();

    const lastPath = useRef(null);
    const userAction = useRef(false);

    useEffect(()=>{
        if(panelData.panel){
            history.push(`/${panelData.panel}`);
        }
    }, [panelData.panel, history])

    const toggleMenu = useCallback((open)=>{
        dispatch({ type: "SET_ACCESSIBLE",
            payload: {
                ...accessible,
                menuOpen: open
            }
        });
    }, [accessible,dispatch ]);

    const updateStack = useCallback((newLocation, pop)=>{

        let currentLocation;
        for (let [key] of Object.entries(panelData.stackOrder)) {
            if(panelData.stackOrder[key] === 'active') currentLocation = key;
        }

        let newStackOrder = { ...defaultOrder };

        newStackOrder[newLocation] = 'active';
        newStackOrder[currentLocation] = 'prev';
        let activeFound = false;
        for (let [key] of Object.entries(newStackOrder)) {

            if(key !== newLocation && key !== currentLocation) newStackOrder[key] = '';

            if(activeFound){
                newStackOrder[key] = 'next';
                activeFound = false;
            }
            if(key === newLocation) activeFound = true;
        }

        if(currentLocation !== newLocation){

            if(newLocation === 'introText') newLocation = ' ';

            dispatch({ type: "SET_PANEL_DATA",
                payload: {
                    fromTo: {from: currentLocation, to: newLocation},
                    stackOrder: newStackOrder,
                    skipAnimation: false,
                    panel: !pop ? newLocation : null
                }
            });

            setTimeout(()=>{
                userAction.current = false;
            }, 10)
        }

    }, [panelData.stackOrder, dispatch ]);

    const menuClick = useCallback((newLocation, historyNav)=>{

        userAction.current = true;

        if(!parallax.current) return;

        let activeNode = parallax.current.querySelector('div[data-status="active"]')

        if((accessible.exploring && activeNode) || (historyNav && activeNode)){

            if(panelData.stackOrder['project-inspire'] === 'active'){

                let telephoneStage = document.querySelector('.telephoneStage');
                telephoneStage.classList.remove(parallaxStyles.snapType);
                forceRedraw()

                gsap.to(activeNode, {duration: 0.5, scrollTo: {x: activeNode.offsetLeft}, onComplete: ()=>{
                        telephoneStage.classList.add(parallaxStyles.snapType)
                        dispatch({ type: "SET_ACCESSIBLE",
                            payload: {
                                ...accessible,
                                exploring: false,
                                topExit: true,
                                menuOpen: false
                            }
                        });
                        updateStack(newLocation);
                    }})

            } else {
                gsap.to(activeNode, {duration: 0.5, scrollTo: {y: activeNode.offsetTop}, onComplete: ()=>{
                        dispatch({ type: "SET_ACCESSIBLE",
                            payload: {
                                ...accessible,
                                exploring: false,
                                topExit: true,
                                menuOpen: false
                            }
                        });
                        updateStack(newLocation);
                    }})
            }


        } else {
            updateStack(newLocation);
        }

    },[accessible, dispatch, panelData.stackOrder, updateStack])

    useEffect(()=>{

        let unregisterListener = history.listen((location, action) => {
            if(lastPath.current !== history.location.pathname && !userAction.current){
                menuClick(location.pathname.replace('/',''), true)
            }
            lastPath.current = history.location.pathname;
        });

        return ()=>unregisterListener();

    }, [history, updateStack, menuClick]);


    function animateScroll(e) {

        if(!accessible.siteEntered) return

        if(animationCount === 0 && !accessible.exploring){

            let horizontal = false;

            /** MICROSOFT DOES NOT SIDE SWIPE HORIZONTAL... Implemented a touch/click drag slider **/

            if(Math.abs(e.touchDeltaX) > Math.abs(e.touchDeltaY) || Math.abs(e.deltaX) > Math.abs(e.deltaY)){
                horizontal = true;
            }

            if(!horizontal){

                userAction.current = true;

                /** find our active panel **/
                let objectSize = 0;
                let activeIndex = 0;
                Object.keys(panelData.stackOrder).forEach((key, index)=>{
                    if(panelData.stackOrder[key] === 'active') activeIndex = index;
                    objectSize++;
                });

                /** determine which direction we are going **/

                let deltaY = e.touchDeltaY || -e.deltaY || e.wheelDelta;

                /** Spotlight needs some wiggle room for side scrolling and accidental horizontal during side scrolling,
                 * touch delta barely clears 10, while destktop can clear much more **/
                if(panelData.stackOrder.spotlight === 'active' && (
                    (e.touchDeltaY < 5 && e.touchDeltaY > -5) || // touch move hits only about 10 tops
                    (!e.touchDeltaY && (deltaY < 20 && deltaY > -20)) // if not touch move, check for normal move
                )) {
                    return;
                }


                activeIndex = deltaY > 0 ? activeIndex - 1 : activeIndex + 1;

                if(activeIndex < 0) activeIndex = 0;
                if(activeIndex >= objectSize) activeIndex = objectSize-1

                /** update stack order based on new active panel **/
                let newStackOrder = { ...defaultOrder };

                let routeUpdate = '';
                Object.keys(newStackOrder).forEach((key, index)=>{
                    if(index === activeIndex - 1) newStackOrder[key] = 'prev';
                    else if(index === activeIndex){
                        newStackOrder[key] = 'active';
                        routeUpdate = key;
                    }
                    else if(index === activeIndex + 1) newStackOrder[key] = 'next';
                    else newStackOrder[key] = ''
                });

                if(routeUpdate === 'introText') routeUpdate = ' ';

                dispatch({ type: "SET_PANEL_DATA",
                    payload: {
                        fromTo: {},
                        stackOrder: newStackOrder,
                        skipAnimation: false,
                        panel: routeUpdate
                    }
                });

                userAction.current = false;


            }

        }

    }

    function buildMacroPages (){

        let macros = ['Martellus','Deadmau5','Cache','Brooke'];
        return macros.map((macro, macroIndex)=>{
            return  <MacroPage key={macro} macro={macro} status={panelData.stackOrder[macro.toLowerCase()]}
                               menuClick={menuClick}
                               onEnd={onEnd}
                               zIndex={7-macroIndex}/>
        })

    }


    function onEnd(panel, nextPanel){

        userAction.current = true;

        if(nextPanel){

            dispatch({ type: "SET_PANEL_DATA",
                payload: {
                    fromTo: {},
                    stackOrder: analyzeStack(panel, defaultOrder),
                    skipAnimation: true,
                    panel: panel
                }
            });


        }

        dispatch({ type: "SET_ACCESSIBLE",
            payload: {
                ...accessible,
                exploring: false,
                topExit: nextPanel !== true
            }
        });


        /** just ensuring stackorder and skip are completed before re-activation or it will autoscroll to the next panel **/
        setTimeout(()=>{
            userAction.current = false;
        }, 1000)

    }

    function addCarats(){

        if(!accessible.exploring && panelData.stackOrder['customize-yours'] !== 'active'){

            return (
                <div aria-hidden={accessible.siteEntered} aria-label={'Scroll down to navigate the site'} className={styles.carats}>
                    <ScrollIcon/>
                </div>
            )

        }

    }

    function forceHidden(){
        if(accessible.siteEntered && (window.location.pathname === '/')) return true;
        if(!accessible.siteEntered && window.location.pathname !== '/') return true
        if(accessible.modalOpen) return true
        return false;
    }

    function buildView(){

        return (
            <div aria-hidden={!accessible.siteEntered} ref={parallax}>

                {!accessible.exploring && (
                    <HeaderLogo ariaHidden={forceHidden()} menuClick={()=>menuClick('introText')}/>
                )}

                <div className={styles.menuToggle} >

                    <div aria-hidden={forceHidden()} tabIndex={accessible.siteEntered && !accessible.modalOpen ? 0 : null}
                         role={'button'} aria-label={!accessible.menuOpen ? 'Open menu' : 'Close menu'} onKeyDown={buttonClick} className={styles.menuIcon}
                         ref={toggle} onClick={()=>toggleMenu(!accessible.menuOpen)}>
                        <NavToggle menuOpen={accessible.menuOpen}/>
                    </div>
                    {accessible.menuOpen && <NavMenu toggle={toggle.current} linkTo={menuClick} close={()=>toggleMenu(false)}/>}
                </div>

                <BackgroundMusic hide={forceHidden()}/>

                <IntroText status={panelData.stackOrder.introText} zIndex={10}
                           nextPanel={(()=>menuClick('featured'))}/>

                <LandingVideo zIndex={1}/>

                <Featured status={panelData.stackOrder.featured}  linkTo={menuClick}  zIndex={9}
                          nextPanel={(()=>menuClick('contest'))}
                          prevPanel={(()=>menuClick('introText'))}/>



                <OriginalsUGC status={panelData.stackOrder['contest']} zIndex={8}
                              menuClick={menuClick}
                              nextPanel={(()=>menuClick('featured'))}
                             prevPanel={(()=>menuClick('martellus'))}/>


                {buildMacroPages()}

                <MicroInfluencers status={panelData.stackOrder.spotlight} zIndex={3}
                                  nextPanel={(()=>menuClick('project-inspire'))}
                                  prevPanel={(()=>menuClick('brooke'))}/>


                <Telephone status={panelData.stackOrder['project-inspire']} zIndex={2} menuClick={menuClick}
                                nextPanel={(()=>menuClick('customize-yours'))}
                                prevPanel={(()=>menuClick('spotlight'))}/>

                <CustomWraps macro={'customize-yours'} status={panelData.stackOrder['customize-yours']}
                             onEnd={onEnd} zIndex={1} menuClick={menuClick}
                             prevPanel={(()=>menuClick('project-inspire'))}/>

                {addCarats()}

                <ScrollEvent target={window} scroll={animateScroll} disabled={accessible.exploring}/>

            </div>
        )

    }

    return buildView()
};

export default HomeParallax